import { animate, style, transition, trigger } from '@angular/animations';

/**
 * `:leave`/`*ngIf` transitions play reliably when the animated element is removed by the
 * SAME component's own change detection (e.g. editable-table's own export/error modals,
 * deleteconfirmationmodal, background-operation-result-modal). They do NOT reliably play
 * when a whole child component (e.g. dynamicform/dynamicdetail) is torn down by a PARENT's
 * structural directive - see the `overlayFadeManagedAnimation`/`panelSilkManagedAnimation`
 * variants below for that case.
 */

/** Fades a modal's semi-transparent backdrop in/out. */
export const overlayFadeAnimation = trigger('overlayFade', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('180ms ease-out', style({ opacity: 1 })),
  ]),
  transition(':leave', [
    animate('150ms ease-in', style({ opacity: 0 })),
  ]),
]);

/** Gives a modal panel a soft scale/slide entrance and exit. */
export const panelSilkAnimation = trigger('panelSilk', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translate3d(0, 8px, 0) scale(0.97)' }),
    animate(
      '220ms cubic-bezier(0.16, 1, 0.3, 1)',
      style({ opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' })
    ),
  ]),
  transition(':leave', [
    animate(
      '160ms ease-in',
      style({ opacity: 0, transform: 'translate3d(0, 4px, 0) scale(0.98)' })
    ),
  ]),
]);

/** Delay (ms) to wait for the 'closing' transition above before actually removing/closing a modal. */
export const MODAL_CLOSE_ANIMATION_MS = 180;

/**
 * Cross-component variants (dynamicform/dynamicdetail) — only handle the mount fade-in.
 * The close fade is done with a plain CSS transition (toggled via the `isClosing` class
 * bindings in the component templates) instead of an Angular animation state, because
 * Angular's animation engine replaying/holding onto a leave-style transition across the
 * parent's structural-directive removal is what caused the earlier flash/ghost-overlay
 * glitches. A bound value that never changes after mount (always 'open') means Angular
 * never has anything left to animate when the parent actually removes the element.
 */
export const overlayFadeManagedAnimation = trigger('overlayFadeManaged', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('180ms ease-out', style({ opacity: 1 })),
  ]),
]);

export const panelSilkManagedAnimation = trigger('panelSilkManaged', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translate3d(0, 8px, 0) scale(0.97)' }),
    animate(
      '220ms cubic-bezier(0.16, 1, 0.3, 1)',
      style({ opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' })
    ),
  ]),
]);
